/**
 * tone-presets.ts - built-in tone presets for the Tone tab preset dropdown.
 *
 * Each preset is a `{ id, nameKey, patch }` object where `patch` is a
 * partial DemoState applied via `store.patch()` when the user selects it.
 * The sentinel preset `'custom'` represents the user's current untracked
 * state and is never applied as a patch (it is only shown as the label
 * when the user has diverged from a named preset).
 *
 * PRESET_CONTROLLED_KEYS is the canonical list of tone fields. When the
 * user manually edits one of those fields, the store patch should include
 * `tonePreset: 'custom'` to signal that the current settings no longer
 * match any named preset.
 *
 * Usage:
 *   import { TONE_PRESETS, PRESET_CONTROLLED_KEYS } from './tone-presets';
 *
 *   // Apply a named preset:
 *   const p = TONE_PRESETS.find(p => p.id === selectedId);
 *   if (p && p.id !== 'custom') store.patch({ tonePreset: p.id, ...p.patch });
 *
 *   // Auto-revert on manual edit:
 *   function onSliderChange(field: string, value: number) {
 *     const patch: Partial<DemoState> = { [field]: value };
 *     if (PRESET_CONTROLLED_KEYS.has(field as keyof DemoState)) {
 *       patch.tonePreset = 'custom';
 *     }
 *     store.patch(patch);
 *   }
 */

import type { DemoState } from '../state';
import { TONE_METHOD_FIELDS } from '../util/tone-method-fields';

export type TonePreset = {
  /** Stable ID - stored in `state.tonePreset`. */
  id: string;
  /** i18n key for the display name. */
  nameKey: string;
  /**
   * State fields this preset applies. Undefined for the 'custom' sentinel
   * (which is never dispatched as a patch).
   */
  patch?: Partial<DemoState>;
};

/**
 * Built-in preset definitions in display order.
 * The 'custom' entry is the sentinel shown when the user diverges from any
 * named preset; it must appear first so the dropdown defaults to it when
 * tonePreset === 'custom'.
 */
export const TONE_PRESETS: TonePreset[] = [
  {
    id: 'custom',
    nameKey: 'preset.custom.name',
    // No patch - sentinel only.
  },
  {
    id: 'newspaper',
    nameKey: 'preset.newspaper.name',
    patch: {
      family: 'A',
      cellSize: 4,
      stampType: 'circle',
      stampCoverage: 0.9,
      algorithmJitter: 0.075,
      contrast: 20,
    },
  },
  {
    id: 'softHalftone',
    nameKey: 'preset.softHalftone.name',
    patch: {
      family: 'A',
      cellSize: 8,
      stampType: 'circle',
      stampCoverage: 0.9,
      algorithmJitter: 0.075,
      contrast: 0,
      brightness: 5,
    },
  },
  {
    id: 'sketchDither',
    nameKey: 'preset.sketchDither.name',
    patch: {
      family: 'C',
      algorithmA: 'atkinson',
      contrast: 10,
    },
  },
  {
    id: 'punchyDither',
    nameKey: 'preset.punchyDither.name',
    patch: {
      family: 'C',
      algorithmA: 'floyd-steinberg',
      algorithmJitter: 0.3,
      contrast: 30,
    },
  },
];

/**
 * Tone controls only - anything the user can dial in the Tone tab and
 * would expect to round-trip via Save/Export/Import. EXCLUDES source-adjust,
 * image binaries, segmentation, and UI state.
 */
export const PRESET_CONTROLLED_KEY_LIST = TONE_METHOD_FIELDS;

export const PRESET_CONTROLLED_KEYS: ReadonlySet<keyof DemoState> = new Set(
  PRESET_CONTROLLED_KEY_LIST,
);
